import React, { useMemo } from 'react';

import { currency, getFormattedAmount } from '../../helpers/currency';

import { Table, TableCell, Box, Section, TextField } from 'components';
import { Order, paymentMethodLabels } from 'data';
import { formatDateToMed } from 'helpers';
import useMediaQuery from 'hooks/mediaQuery/useMediaQuery';

const theadData = (isMobile: boolean) => {
  const mobileStyle = isMobile ? 'text-xs py-1 px-1' : 'w-[3%]';
  return [
    { Name: 'Refund Type', className: `${mobileStyle} text-left` },
    { Name: 'Refund', className: `${mobileStyle} text-center` },
    { Name: 'Refund Fee', className: `${mobileStyle} text-center` },
    { Name: 'Quantity', className: `${mobileStyle} text-center` },
    { Name: 'Subtotal', className: `${mobileStyle} text-center` },
  ];
};

const getRefundTransactionTypeLabel = (paymentMethodIntent?: string) => {
  const label = paymentMethodIntent ? paymentMethodLabels[paymentMethodIntent] : null;
  return label ? `Refund of ${label} Purchase` : 'Refund';
};

const RefundTransactionDetails = ({
  order,
  vendorName,
  currency,
}: {
  order: Order;
  vendorName: string;
  currency: currency | undefined;
}) => {
  const isMobile = useMediaQuery();
  const transactionType = getRefundTransactionTypeLabel(order.misc?.paymentMethodIntent);

  // Get all refunds and sort by creation date (oldest first)
  const refunds = useMemo(() => {
    if (!order?.refunds) return [];

    return Object.entries(order.refunds)
      .map(([key, refund]: [string, any]) => ({
        key,
        refundType: refund.request?.refundType || '',
        refundTotalAmount: refund.refundTotalAmount || 0,
        refundFee: refund.refundFee || 0,
        createdOn: refund.createdOn || order.createdOn,
        itemUuids: refund.itemUuids || refund.request?.itemUuids || [],
      }))
      .filter((refund) => refund.refundTotalAmount > 0)
      .sort((a, b) => new Date(a.createdOn).getTime() - new Date(b.createdOn).getTime());
  }, [order]);

  // Calculate totals
  const totalRefund = useMemo(
    () => refunds.reduce((sum, refund) => sum + refund.refundTotalAmount, 0),
    [refunds],
  );
  const totalRefundFee = useMemo(
    () => refunds.reduce((sum, refund) => sum + refund.refundFee, 0),
    [refunds],
  );

  const displayDate = refunds.length > 0 ? refunds[0].createdOn : order.createdOn;

  if (refunds.length === 0) {
    return (
      <div className='text-center py-8'>
        <p className='text-gray-500'>No refund information available</p>
      </div>
    );
  }

  return isMobile ? (
    <>
      <div className={`w-[${window.innerWidth}px] bg-white px-4 pt-2 mt-3 mx-[-24px] pb-3 mb-12`}>
        <div className='mt-2'>
          <div className='pb-3'>
            <TextField value={vendorName} label='Vendor' isDisabled />
          </div>
          <div className='pb-3'>
            <TextField value={formatDateToMed(displayDate)} label='Date' isDisabled />
          </div>
          <div className='pb-3'>
            <TextField value={transactionType} label='Transaction' isDisabled />
          </div>
          <div className='pb-0'>
            <TextField value={order.uuid} label='Order ID' isDisabled />
          </div>
        </div>

        <h2 className='text-lg my-4 text-gray-500'>Refunds</h2>

        {refunds.map((refund) => (
          <div key={refund.key} className='rounded-lg border border-[#e8efef] p-3 mb-4'>
            <div className='flex justify-between items-center mb-2'>
              <div className='font-semibold'>{refund.refundType.toUpperCase()}</div>
            </div>
            <div className='flex justify-between items-center mb-2'>
              <div>Refund</div>
              <div>{getFormattedAmount(refund.refundTotalAmount, currency)}</div>
            </div>
            <div className='flex justify-between items-center mb-2'>
              <div>Refund Fee</div>
              <div>{getFormattedAmount(refund.refundFee, currency)}</div>
            </div>
            <div className='border-b border-gray-200 mb-2'></div>
            <div className='flex justify-between items-center'>
              <div className='font-semibold'>Total</div>
              <div className='font-semibold'>
                {getFormattedAmount(refund.refundTotalAmount, currency)}
              </div>
            </div>
          </div>
        ))}

        <h2 className='text-lg my-4 text-gray-500'>Refund Summary</h2>

        <div className='rounded-lg border border-[#e8efef] p-3'>
          <div className='flex justify-between items-center mb-2'>
            <div>Total Refund</div>
            <div>{getFormattedAmount(totalRefund, currency)}</div>
          </div>
          <div className='flex justify-between items-center mb-2'>
            <div>Refund Fee</div>
            <div>{getFormattedAmount(totalRefundFee, currency)}</div>
          </div>
          <div className='border-b border-gray-200 mb-2'></div>
          <div className='flex justify-between items-center'>
            <div className='font-semibold'>Total</div>
            <div className='font-semibold'>
              {getFormattedAmount(totalRefund + totalRefundFee, currency)}
            </div>
          </div>
        </div>
      </div>
    </>
  ) : (
    <>
      <Box className='p-12 mt-[12px]'>
        <div className='md:flex md:justify-between'>
          <div className='md:w-1/2 md:pr-8'>
            <Section
              title='Details'
              titleClassName='text-xl md:text-3xl'
              className='col-span-12  justify-end'
              description='There are the specifics of your refund.'
            />
          </div>
          <div className='md:w-1/2 md:pr-8'>
            <div className='pb-3'>
              <TextField value={vendorName} label='Vendor' isDisabled />
            </div>
            <div className='pb-3'>
              <TextField value={formatDateToMed(displayDate)} label='Date' isDisabled />
            </div>
            <div className='pb-3'>
              <TextField value={transactionType} label='Transaction' isDisabled />
            </div>
            <div className='pb-3'>
              <TextField value={order.uuid} label='Order ID' isDisabled />
            </div>
          </div>
        </div>

        <div className='w-full h-[1px] bg-[#E8EAED] mt-6 lg:mb-12' />

        <Table theadData={theadData(isMobile)} className='w-full rounded-lg mb-12'>
          {refunds.map((refund) => {
            const quantity = refund.itemUuids.length || 1;
            return (
              <tr key={refund.key}>
                <TableCell
                  className={`${
                    isMobile ? 'py-1 px-1 text-xs' : 'py-4 px-6'
                  } text-custom-primary text-base`}
                  data={refund.refundType.toUpperCase()}
                />
                <TableCell
                  className={`${isMobile && 'text-xs px-1'} text-center text-base text-[#6F7FAF]`}
                  data={getFormattedAmount(refund.refundTotalAmount, currency)}
                />
                <TableCell
                  className={`${isMobile && 'text-xs px-1'} text-center text-base text-[#6F7FAF]`}
                  data={getFormattedAmount(refund.refundFee, currency)}
                />
                <TableCell
                  className={`${isMobile && 'text-xs px-1'} text-center text-[#6F7FAF]`}
                  data={quantity}
                />
                <TableCell
                  className={`${
                    isMobile ? 'text-xs px-1' : 'py-4 px-6'
                  } text-base text-[#6F7FAF] text-center`}
                  data={getFormattedAmount(refund.refundTotalAmount, currency)}
                />
              </tr>
            );
          })}
        </Table>

        <div className='md:flex md:justify-start'>
          <div className='md:w-1/2 md:pr-8'>
            <Section
              title='Refund Summary'
              titleClassName='text-xl md:text-3xl'
              className='col-span-12  justify-end'
            />
          </div>
          <div className='md:w-1/3 md:pr-8'>
            <div className='flex justify-between text-[#6F7FAF]  pb-3'>
              <p>Total Refund</p>
              <p>{getFormattedAmount(totalRefund, currency)}</p>
            </div>
            <div className='flex justify-between text-[#6F7FAF]  pb-3'>
              <p>Refund Fee</p>
              <p>{getFormattedAmount(totalRefundFee, currency)}</p>
            </div>
            <div className='w-full h-[1px] bg-[#E8EAED] mb-3' />

            <div className='flex justify-between pb-3'>
              <p className='pb-6 font-bold  text-lg'>Total</p>
              <p className='pb-6 font-bold  text-lg'>
                {getFormattedAmount(totalRefund + totalRefundFee, currency)}
              </p>
            </div>
          </div>
        </div>
      </Box>
    </>
  );
};

export default RefundTransactionDetails;
